import { supabase } from './supabase'

/**
 * Does the database look the way this build of the app expects?
 *
 * Three times now a migration has quietly not arrived, and every time the app
 * carried on as if it had. The enum default in 0022 would have failed every
 * monthly invoice inside a nightly job nobody reads. The worker stood still
 * for two days behind a cheerful "wordt gelezen". And 0026 to 0028 turned out
 * never to have run at all, weeks after they were called done.
 *
 * The shape of all three is the same: the app assumes a database that is not
 * there, and nothing in the system can notice the difference. So the app now
 * carries the list of what it needs, the database carries the list of what it
 * has (0031), and the difference is a subtraction rather than an evening.
 *
 * The list below is the contract. Adding a migration means adding its number
 * here in the same change — if you forget, the check simply does not cover it,
 * which is the safe direction to fail in.
 */
export const EXPECTED_MIGRATIONS = [
  '0001', '0005', '0006', '0007', '0009', '0011', '0013', '0015', '0016',
  '0017', '0018', '0019', '0020', '0021', '0022', '0023', '0024', '0025',
  '0026', '0027', '0028', '0029', '0030', '0031', '0032', '0033', '0034', '0035', '0036', '0037', '0038', '0039', '0040', '0041', '0042', '0043', '0044', '0045',
] as const

export interface SchemaState {
  /** Numbers this build needs that the database does not have. */
  missing: string[]
  /**
   * True when the ledger itself is absent — 0031 has not been run, so nothing
   * can be concluded. Deliberately distinct from "nothing is missing": an
   * unanswerable question must never read as a clean bill of health.
   */
  unknown: boolean
}

export async function checkSchema(): Promise<SchemaState> {
  const { data, error } = await supabase.from('schema_migrations').select('version')

  // The ledger is missing, or unreadable. Say so as its own state.
  if (error || !data) return { missing: [], unknown: true }

  const have = new Set(data.map((row) => String(row.version)))
  return {
    missing: EXPECTED_MIGRATIONS.filter((v) => !have.has(v)),
    unknown: false,
  }
}

/**
 * What to say about it, in words that name the next action.
 *
 * "0026 ontbreekt" is a fact; "plak NU-PLAKKEN-9B.sql" is a fix. The file
 * names are not derivable from the numbers, so the mapping lives here — and
 * an unmapped number still gets named rather than swallowed.
 */
const PASTE_FILES: Record<string, string> = {
  '0026': 'NU-PLAKKEN-9B.sql',
  '0027': 'NU-PLAKKEN-9B.sql',
  '0028': 'NU-PLAKKEN-9B.sql',
  '0029': 'NU-PLAKKEN-10.sql',
  '0030': 'NU-PLAKKEN-11.sql',
  '0031': 'NU-PLAKKEN-12.sql',
  '0032': 'NU-PLAKKEN-13.sql',
  '0033': 'NU-PLAKKEN-14.sql',
  '0034': 'NU-PLAKKEN-15.sql',
  '0035': 'NU-PLAKKEN-16.sql',
  '0036': 'NU-PLAKKEN-17.sql',
  '0037': 'NU-PLAKKEN-17.sql',
  '0038': 'NU-PLAKKEN-18.sql',
  '0039': 'NU-PLAKKEN-19.sql',
  '0040': 'NU-PLAKKEN-20.sql',
  '0041': 'NU-PLAKKEN-21.sql',
  '0042': 'NU-PLAKKEN-22.sql',
  '0043': 'NU-PLAKKEN-23.sql',
  '0044': 'NU-PLAKKEN-24.sql',
  '0045': 'NU-PLAKKEN-25.sql',
}

export function describeSchema(state: SchemaState): string | null {
  if (state.unknown) {
    return 'De databank kan niet zeggen welke migraties gedraaid zijn. Plak NU-PLAKKEN-12.sql.'
  }
  if (state.missing.length === 0) return null

  const files = [...new Set(state.missing.map((v) => PASTE_FILES[v]).filter(Boolean))]
  const which = state.missing.join(', ')

  if (files.length === 0) {
    return `De databank mist migratie ${which}.`
  }
  return `De databank mist migratie ${which}. Plak ${files.join(' en ')} in de SQL-editor.`
}
